export const EQUALS_CARDS_ERROR_MESSAGES_BY_CODE = {
  // Validation errors
  MISSING_FIELDS: {
    en: 'Missing required fields: {fields}',
    fr: 'Champs obligatoires manquants : {fields}',
  },
  INVALID_CARD_TYPE: {
    en: 'Invalid card type. Must be "budget_card" or "individual_card"',
    fr: 'Type de carte invalide. Doit être "budget_card" ou "individual_card"',
  },
  INVALID_STATE: {
    en: 'Invalid state. Must be one of: active, suspended, terminated',
    fr: 'État invalide. Doit être : active, suspended, terminated',
  },
  CARD_REQUEST_NOT_NEEDED: {
    en: 'You may already do this yourself, no request needed.',
    fr: "Vous pouvez déjà le faire vous-même, aucune demande n'est nécessaire.",
  },
  CARD_REQUEST_ALREADY_PENDING: {
    en: 'A request is already pending.',
    fr: 'Une demande est déjà en attente.',
  },
  CARD_REQUEST_ALREADY_HANDLED: {
    en: 'This request has already been handled.',
    fr: "Cette demande n'est plus en attente.",
  },
  CARD_REQUEST_NOT_FOUND: {
    en: 'Card request not found.',
    fr: 'Demande introuvable.',
  },
  CARD_REQUEST_TYPE_MISMATCH: {
    en: 'This request asked for something else.',
    fr: 'Cette demande portait sur autre chose.',
  },
  INVALID_CARD_REQUEST_REFERENT: {
    en: 'This person cannot handle card requests.',
    fr: 'Cette personne ne peut pas traiter les demandes de carte.',
  },
  TRANSACTION_LIMIT_ABOVE_SPENDING_LIMIT: {
    en: 'The per-transaction limit cannot exceed the periodic limit.',
    fr: 'Le plafond par transaction ne peut pas dépasser le plafond périodique.',
  },
  INVALID_CARD_LIMITS: {
    en: 'These limits are invalid.',
    fr: 'Ces plafonds ne sont pas valides.',
  },
  INVALID_PIN: {
    en: 'PIN must be exactly 4 digits',
    fr: 'Le PIN doit comporter exactement 4 chiffres',
  },
  WEAK_PIN: {
    en: 'PIN is too weak. Avoid identical digits or consecutive sequences.',
    fr: 'Le code PIN est trop faible. Évitez les chiffres identiques ou les séquences consécutives.',
  },
  INDIVIDUAL_CARD_LIMIT_EXCEEDED: {
    en: 'This user already has an active individual card for this company.',
    fr: 'Cet utilisateur possède déjà une carte individuelle active pour cette entreprise.',
  },
  INVALID_OTP_FORMAT: {
    en: 'Code must be a 6-digit number',
    fr: 'Le code doit être un nombre à 6 chiffres',
  },
  CARD_NAME_TOO_LONG: {
    en: 'Card name must be 40 characters or fewer',
    fr: 'Le nom de la carte ne doit pas dépasser 40 caractères',
  },
  INVALID_STATUS_FILTER: {
    en: 'Status filter must be "active" or "archived"',
    fr: 'Le filtre de statut doit être "active" ou "archived"',
  },
  INVALID_VIEW: {
    en: 'View must be "own" or "all"',
    fr: 'La vue doit être "own" ou "all"',
  },

  // Business/service errors
  CARD_NOT_FOUND: {
    en: 'Card not found',
    fr: 'Carte introuvable',
  },
  EQUALS_ACCOUNT_NOT_FOUND: {
    en: 'No Equals account found',
    fr: 'Aucun compte Equals trouvé',
  },
  CARD_PRODUCT_NOT_CONFIGURED: {
    en: 'This card type is not enabled on your account yet. Please contact support.',
    fr: "Ce type de carte n'est pas encore activé sur votre compte. Contactez le support.",
  },
  EQUALS_PERSON_NOT_FOUND: {
    en: 'No Equals person found for this user',
    fr: 'Aucun profil Equals trouvé pour cet utilisateur',
  },

  // OTP errors (update_pin / access-token)
  OTP_NOT_FOUND: {
    en: 'OTP not found',
    fr: 'Code OTP introuvable',
  },
  OTP_ALREADY_USED: {
    en: 'Code has already been used',
    fr: 'Le code a déjà été utilisé',
  },
  OTP_MAX_ATTEMPTS: {
    en: 'Maximum attempts exceeded',
    fr: 'Nombre maximum de tentatives dépassé',
  },
  OTP_EXPIRED: {
    en: 'Code has expired',
    fr: 'Le code a expiré',
  },
  OTP_INVALID_CODE: {
    en: 'Invalid code. Remaining attempts: {n}',
    fr: 'Code invalide. Tentatives restantes : {n}',
  },
  OTP_SYSTEM_ERROR: {
    en: 'An error occurred. Please try again later.',
    fr: 'Une erreur est survenue. Veuillez réessayer plus tard.',
  },
};

function normalizeLocale(locale) {
  return locale === 'en' ? 'en' : 'fr';
}

function interpolate(template, params) {
  if (!template || !params) return template;
  return template.replace(/\{(\w+)\}/g, (m, k) => {
    const v = params[k];
    if (v === undefined || v === null) return m;
    if (Array.isArray(v)) return v.join(', ');
    return String(v);
  });
}

function parseRemainingAttemptsFromMessage(error) {
  const msg =
    error?.message || error?.msg || error?.error_message || error?.errorMessage;
  if (!msg) return undefined;

  const en = String(msg).match(/remaining attempts\s*:\s*(\d+)/i);
  if (en?.[1]) return Number(en[1]);

  const fr = String(msg).match(/tentatives restantes\s*:\s*(\d+)/i);
  if (fr?.[1]) return Number(fr[1]);

  return undefined;
}

function extractParams(error) {
  const ctx = error?.error_context || error?.context || {};

  const fields =
    error?.fields ??
    ctx?.fields ??
    ctx?.missing_fields ??
    ctx?.missingFields ??
    undefined;

  const n =
    error?.n ??
    error?.remaining_attempts ??
    error?.remainingAttempts ??
    ctx?.n ??
    ctx?.remaining_attempts ??
    ctx?.remainingAttempts ??
    parseRemainingAttemptsFromMessage(error) ??
    undefined;

  return {fields, n};
}

export function isEqualsCardsApi(api) {
  if (typeof api !== 'string') return false;
  // `equals/card-requests` and `equals/card-limit-requests` do not contain
  // `equals/cards`, so they need their own match or their codes stay unmapped.
  return (
    api.includes('equals/cards') ||
    api.includes('equals/card-requests') ||
    api.includes('equals/card-limit-requests')
  );
}

export function resolveEqualsCardsErrorMessage({api, error, locale} = {}) {
  const loc = normalizeLocale(locale);
  const code = error?.error_code || error?.code || error?.errorCode;
  if (!code) return null;

  // Only apply this mapping to equals/cards endpoints
  if (!isEqualsCardsApi(api)) return null;

  const msg = EQUALS_CARDS_ERROR_MESSAGES_BY_CODE?.[code]?.[loc];
  if (!msg) return null;

  return interpolate(msg, extractParams(error));
}
